/**
 * useTenant Hook
 *
 * Custom hook for accessing tenant state and actions.
 * Wraps the Zustand tenantStore for easier consumption in components.
 *
 * @example
 * ```tsx
 * function TenantSwitcher() {
 *   const { currentTenant, tenants, switchTenant } = useTenant();
 *
 *   return (
 *     <Select value={currentTenant?.id} onValueChange={switchTenant}>
 *       {tenants.map((tenant) => (
 *         <SelectItem key={tenant.id} value={tenant.id}>
 *           {tenant.name}
 *         </SelectItem>
 *       ))}
 *     </Select>
 *   );
 * }
 * ```
 */

import { useTenantStore, Tenant } from '@/stores/tenantStore';

export interface UseTenantReturn {
  // State
  currentTenant: Tenant | null;
  tenants: Tenant[];
  isLoading: boolean;
  error: string | null;

  // Actions
  setTenant: (tenant: Tenant) => void;
  loadTenants: (tenants: Tenant[]) => void;
  switchTenant: (tenantId: string) => void;
  clearTenants: () => void;
}

/**
 * Hook to access tenant state and actions
 */
export const useTenant = (): UseTenantReturn => {
  const currentTenant = useTenantStore((state) => state.currentTenant);
  const tenants = useTenantStore((state) => state.tenants);
  const isLoading = useTenantStore((state) => state.isLoading);
  const error = useTenantStore((state) => state.error);
  const setTenant = useTenantStore((state) => state.setTenant);
  const loadTenants = useTenantStore((state) => state.loadTenants);
  const switchTenant = useTenantStore((state) => state.switchTenant);
  const clearTenants = useTenantStore((state) => state.clearTenants);

  return {
    currentTenant,
    tenants,
    isLoading,
    error,
    setTenant,
    loadTenants,
    switchTenant,
    clearTenants,
  };
};
